import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Minus, Share2, Calculator } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { pokerGameAPI } from './services/pokerGameApi';
import { financialTransactionAPI } from '../../finance/financialTransactionService';
import ConfirmModal from '../../components/ConfirmModal';
import EventSummary from '../../components/common/EventSummary';
import { getAvailableMembers } from '../../events/components/EventAttendance';

/**
 * PokerScoring - Self-contained component for poker game scoring
 *
 * Props:
 * - event: Event object (required)
 * - members: Array of group members (required, for player initialization)
 * - gameId: Optional specific game ID to load (from deep link)
 * - onClose: Callback when closing the scoring interface
 * - onGameComplete: Callback when game is completed
 *
 * This component handles all game operations internally:
 * - Checks for existing games for the event
 * - Creates new game if needed
 * - Handles all CRUD operations
 */
const PokerScoring = ({ event, members, gameId: targetGameId, gameCommonId, onClose, onGameComplete, onCopyLink, onCancel, isScorer = true, paused = false }) => {
  const { theme } = useTheme();
  const { squadId } = useParams(); // Get groupId from URL if available

  // Extract groupId from event or URL (needed for settlement creation)
  const groupId = event?.ownerId || squadId;

  // Local state for game data (fetched from API)
  const [game, setGame] = useState(null);
  const [gameFinished, setGameFinished] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null });
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Game settings
  const [gameSettings, setGameSettings] = useState({
    chipValue: 1, // 1 chip = x dollars
    minimumBuyIn: 100, // Minimum buy-in in chips
    ante: 0, // Ante amount
    buyIn: 100 // Buy-in amount in chips
  });

    // Generate default table name: Poker-MM/DD/YYYY
    const getDefaultTableName = () => {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `Poker-${month}/${day}/${year}`;
    };
  
    // Get display name - use first name only, full name if duplicate first names
    const getDisplayName = (player) => {
      const firstName = player.name.split(' ')[0];
      // Count how many players have the same first name
      const duplicateCount = players.filter(p => p.name.split(' ')[0] === firstName).length;
      return duplicateCount > 1 ? player.name : firstName;
    };

  // true when editing should be disabled (game completed OR current user is not the scorer OR game is paused)
  const readOnly = isViewMode || !isScorer || paused;

  // Initialize player data - will be populated from available members when game loads
  const [players, setPlayers] = useState([]);

  // Initialize/load game when component mounts
  useEffect(() => {
    const initializeGame = async () => {
      if (!event) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        let gameToLoad = null;

        // If GameComponent already resolved the game_common_id, load it directly
        if (gameCommonId) {
          gameToLoad = await pokerGameAPI.getById(gameCommonId);
        }

        // If a specific deep-link game ID was provided, try that
        if (!gameToLoad && targetGameId) {
          gameToLoad = await pokerGameAPI.getById(targetGameId);
        }

        // Fall back to event-based lookup
        if (!gameToLoad) {
          const existingGames = await pokerGameAPI.getByEventId(event.id);

          const activeGames = (existingGames || []).filter(g =>
            g.status !== 'completed' && g.status !== 'finished'
          );

          if (activeGames.length > 0) {
            gameToLoad = activeGames[0];
          }
        }

        // If still no game, create a new one
        if (!gameToLoad) {
          gameToLoad = await pokerGameAPI.create({
            eventId: event.id,
            tableName: getDefaultTableName(),
            gameType: 'texas_holdem',
            smallBlind: 0,
            bigBlind: 0,
            chipValue100: 100,
            minimumBuyins: 100,
            buyinChipCount: 100,
            payoutCurrency: 'dollars',
          });
        }

        // Promote to in_progress if game_common was created with 'setup' status
        if (gameToLoad.status === 'setup') {
          await pokerGameAPI.update(gameToLoad.id, { status: 'in_progress' });
          gameToLoad = { ...gameToLoad, status: 'in_progress' };
        }

        setGame(gameToLoad);
        setGameFinished(gameToLoad.pokerTableStatus === 'finished' || gameToLoad.status === 'completed');
        setIsViewMode(gameToLoad.status === 'completed');
      } catch (error) {
        console.error('[PokerScoring] Failed to initialize game:', error);
        toast.error('Failed to load poker game');
      } finally {
        setLoading(false);
      }
    };

    initializeGame();
  }, [event?.id, targetGameId, gameCommonId]);

  // Load game settings and players when game changes - DATABASE IS THE ONLY SOURCE OF TRUTH
  useEffect(() => {
    if (!game) {
      return;
    }


    // Load game settings
    const loadedChipValue = game.chipValue100 / 100;
    const loadedBuyInChips = game.buyinChipCount || 100;
    const loadedAnte = game.smallBlind || 0;
    const loadedMinimumBuyIn = game.minimumBuyins || 100;

    setGameSettings({
      chipValue: loadedChipValue,
      minimumBuyIn: loadedMinimumBuyIn,
      ante: loadedAnte,
      buyIn: loadedBuyInChips
    });

    // ALWAYS load players from database results - this is the ONLY source of truth
    if (game.results && game.results.length > 0) {

      const loadedPlayers = game.results.map(result => {
        // Database now stores RAW COUNTS, not dollar amounts!
        const buyins = result.initialBuyin;     // Already a count
        const leftoutChips = result.finalChips;  // Already a count

        // Get player name from result.user or members array
        const userFirstName = result.user?.firstName || result.user?.first_name || '';
        const userLastName = result.user?.lastName || result.user?.last_name || '';
        const playerName = result.user
          ? `${userFirstName} ${userLastName}`.trim() || 'Unknown'
          : `Player ${result.userId}`;

        return {
          id: result.userId,
          name: playerName,
          buyins: buyins || 1,
          leftoutChips: leftoutChips
        };
      });

      loadedPlayers.sort((a, b) => a.name.localeCompare(b.name));
      setPlayers(loadedPlayers);
    } else {
      // No results in database yet - show empty, will be populated by sync effect
      setPlayers([]);
    }
  }, [game]); // Reload when game changes



  // Sync with attendance changes (add/remove players based on availability).
  // IMPORTANT: Must NOT call setGame() — that would put `game` in deps and cause
  // an infinite loop. setPlayers() with a functional update is safe here.
  useEffect(() => {
    if (!game) return;
    if (game.status === 'completed') return;

    const availableMembers = getAvailableMembers(members || [], event);
    const availableIds = new Set(availableMembers.map(m => String(m.id)));

    // Members not yet in the DB (game.results is the snapshot from last load)
    const dbIds = new Set((game.results || []).map(r => String(r.userId)));
    const toAddToDb = availableMembers.filter(m => !dbIds.has(String(m.id)));

    const syncPlayers = async () => {
      // Add newly available members to DB
      for (const member of toAddToDb) {
        try {
          await pokerGameAPI.upsertResult(game.id, member.id, { initialBuyin: 1, finalChips: 0 });
        } catch (err) {
          if (err.message?.includes('Poker table not found') || err.message?.includes('404')) {
            console.warn('[PokerScoring] Poker table not found — stopping attendance sync');
            break;
          }
          if (!err.message?.includes('Unique constraint')) {
            console.error('[PokerScoring] Failed to add player:', member.id, err);
          }
        }
      }

      // 2. Remove from DB: members who are no longer available and have only default values
      const toRemoveFromDb = (game.results || []).filter(r => {
        const isAvailable = availableIds.has(String(r.userId));
        const hasActivity = parseInt(r.initialBuyin) > 1 || parseFloat(r.finalChips || 0) > 0;
        return !isAvailable && !hasActivity;
      });
      for (const result of toRemoveFromDb) {
        try {
          await pokerGameAPI.deleteResult(game.id, result.id);
          setGame(prev => prev ? { ...prev, results: prev.results.filter(r => r.id !== result.id) } : prev);
        } catch (err) {
          console.error('[PokerScoring] Failed to remove unavailable player from DB:', result.userId, err);
        }
      }

      // 3. Sync players state: remove unavailable players (only if no real game activity),
      //    and add available members not yet in the list.
      setPlayers(prev => {
        // Remove members who are no longer available AND have only default values
        const filtered = prev.filter(p => {
          const isAvailable = availableIds.has(String(p.id));
          const hasActivity = p.buyins > 1 || parseFloat(p.leftoutChips || 0) > 0;
          return isAvailable || hasActivity;
        });

        // Add available members missing from the current players list
        const currentIds = new Set(filtered.map(p => String(p.id)));
        const newEntries = availableMembers
          .filter(m => !currentIds.has(String(m.id)))
          .map(m => ({
            id: m.id,
            name: m.name || `Player ${m.id}`,
            buyins: 1,
            leftoutChips: 0
          }));

        return [...filtered, ...newEntries].sort((a, b) => a.name.localeCompare(b.name));
      });
    };

    syncPlayers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, members, game?.id, game?.status]);

  // Save individual player result to database
  // Using game prop directly - props are always current, no stale closure issues
  const savePlayerResult = async (player) => {
    if (!game?.id) {
      toast.error('Cannot save: Game not loaded yet');
      return;
    }

    try {
      // Send RAW COUNTS to backend - no calculations!
      // Backend will calculate dollar amounts using game settings from database

      await pokerGameAPI.upsertResult(game.id, player.id, {
        initialBuyin: player.buyins,                  // Raw buyin count (not dollars!)
        finalChips: parseFloat(player.leftoutChips) || 0  // Raw chip count (not dollars!) - supports decimals
      });

      toast.success(`Saved: ${player.buyins} buyins`);
    } catch (error) {
      console.error('Error saving player result:', error);
      toast.error('Failed to save: ' + error.message);
    }
  };

  // Save game settings to database (stored in game_poker_table)
  const saveGameSettings = async (newSettings) => {
    if (!game?.id) {
      return;
    }

    try {
      await pokerGameAPI.updateTable(game.id, {
        chipValue100: Math.round((newSettings.chipValue || 1) * 100),
        buyinChipCount: newSettings.buyIn || 100,
      });
    } catch (error) {
      console.error('Error saving game settings:', error);
      toast.error('Failed to save game settings');
    }
  };

  // Update game settings and save immediately
  const updateGameSettings = (field, value) => {
    const newValue = value === '' ? '' : Math.max(0, value);
    const newSettings = {
      ...gameSettings,
      [field]: newValue
    };
    setGameSettings(newSettings);

    // Save to database immediately if game exists
    if (game?.id) {
      saveGameSettings(newSettings);
    }
  };

  // Get total buyins for a player
  const getTotalBuyIns = (player) => {
    return player.buyins === '' ? 0 : player.buyins;
  };

  // Calculate result in chips: leftout chips - buyins * buy in chips
  const calculateResultInChips = (player) => {
    const totalBuyIns = getTotalBuyIns(player);
    const leftout = player.leftoutChips === '' ? 0 : parseFloat(player.leftoutChips) || 0;
    const buyIn = gameSettings.buyIn === '' ? 0 : parseFloat(gameSettings.buyIn) || 0;
    return leftout - (totalBuyIns * buyIn);
  };

  // Calculate result in dollars
  const calculateResult = (player) => {
    const resultInChips = calculateResultInChips(player);
    const chipValue = gameSettings.chipValue === '' ? 0 : gameSettings.chipValue;
    return resultInChips * chipValue;
  };

  // Update player field. Pass skipSave=true to update state without triggering an API call
  // (used by the leftoutChips onChange handler to avoid racing saves on every keystroke).
  const updatePlayer = (playerId, field, value, { skipSave = false } = {}) => {
    const updatedPlayers = players.map(player =>
      player.id === playerId
        ? {
            ...player,
            [field]: value === '' ? '' : (
              // For leftoutChips, allow decimals (keep as is)
              field === 'leftoutChips' ? value : Math.max(0, value)
            )
          }
        : player
    );
    setPlayers(updatedPlayers);

    if (!skipSave) {
      // Save the updated player immediately if game exists
      const updatedPlayer = updatedPlayers.find(p => p.id === playerId);
      if (updatedPlayer && game?.id) {
        savePlayerResult(updatedPlayer);
      }
    }
  };

  // Calculate summary
  const summary = {
    totalBuyIns: players.reduce((sum, p) => sum + getTotalBuyIns(p), 0),
    totalLeftoutChips: players.reduce((sum, p) => sum + (parseFloat(p.leftoutChips) || 0), 0),
    totalResult: players.reduce((sum, p) => sum + calculateResult(p), 0),
    totalResultInChips: players.reduce((sum, p) => sum + calculateResultInChips(p), 0)
  };

  // Refresh game data from backend
  const handleRefresh = async () => {
    if (!game?.id) {
      toast.error('No game ID found');
      return;
    }

    try {
      setRefreshing(true);

      // Reload the game from database
      const refreshedGame = await pokerGameAPI.getById(game?.id);

      // Update game state - this will trigger the useEffect to reload all settings and players
      setGame(refreshedGame);
      setGameFinished(refreshedGame.pokerTableStatus === 'finished' || refreshedGame.status === 'completed');
      setIsViewMode(refreshedGame.status === 'completed');

      toast.success('Data refreshed');
    } catch (error) {
      console.error('[PokerScoring] Error refreshing game:', error);
      toast.error('Failed to refresh: ' + error.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleFinishGame = async () => {
    if (!game?.id) {
      toast.error('No game ID found!');
      return;
    }

    try {
      setSaving(true);

      // Persist finished state to poker table — survives refresh
      await pokerGameAPI.updateTable(game?.id, { status: 'finished' });

      setGameFinished(true);
      toast.success('Game finished! Now enter leftout chips for each player.');
    } catch (error) {
      console.error('Error finishing game:', error);
      toast.error('Failed to finish game: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Mark game as completed and create settlement
  const handleMarkComplete = async () => {
    if (!game?.id) {
      toast.error('No game ID found!');
      return;
    }

    // Re-verify balance here, not just at button-render time.
    // State could have changed between when Complete was clicked and when the
    // modal Confirm button was pressed (e.g. a pending onBlur save hadn't landed yet).
    const currentTotalResultInChips = players.reduce((sum, p) => sum + calculateResultInChips(p), 0);
    if (Math.abs(currentTotalResultInChips) >= 0.01) {
      toast.error('Chips are not balanced. Please correct leftout chips before completing.');
      return;
    }

    const playerResults = players
      .filter(p => p.buyins > 0)
      .map(player => ({
        userId: player.id,
        netAmount: calculateResult(player)
      }));

    const hasNonZeroResults = playerResults.some(r => Math.abs(r.netAmount) > 0.01);

    if (!hasNonZeroResults) {
      toast.error('No non-zero results to settle!');
      return;
    }

    // Get eventId from event prop or from the loaded game object
    const eventId = event?.id || game?.eventId;

    if (!eventId) {
      console.error('[PokerScoring] Missing event ID. Event:', event, 'Game:', game);
      toast.error('No event ID found! Cannot create settlement.');
      return;
    }

    if (!groupId) {
      console.error('[PokerScoring] Missing group ID. GroupId:', groupId, 'Event:', event);
      toast.error('No group ID found! Cannot create settlement.');
      return;
    }


    try {
      setSaving(true);

      // Flush all player results to DB before completing — this ensures we persist
      // the very latest local state regardless of whether onBlur saves have landed.
      await Promise.all(
        players
          .filter(p => p.buyins > 0)
          .map(player =>
            pokerGameAPI.upsertResult(game?.id, player.id, {
              initialBuyin: player.buyins,
              finalChips: parseFloat(player.leftoutChips) || 0,
            })
          )
      );

      // Update game_common status to completed
      await pokerGameAPI.update(game?.id, { status: 'completed' });

      // Update poker table status to completed
      await pokerGameAPI.updateTable(game?.id, { status: 'completed' });

      // Create settlement
      const baseTitle = event?.subject || game?.tableName || 'Poker Game';
      const gameTitle = `${baseTitle} (Game #${game?.id})`;
      await financialTransactionAPI.createPokerSettlement(eventId, groupId, gameTitle, playerResults);

      // Notify parent that game is complete (to update event status)
      if (onGameComplete) {
        await onGameComplete(eventId);
      } else {
        console.warn('[PokerScoring] onGameComplete callback not provided!');
      }

      setIsViewMode(true); // Switch to view mode
      toast.success('Game marked as complete! Settlement created in Expenses tab.');
    } catch (error) {
      console.error('Error marking game complete:', error);
      toast.error('Failed to mark complete: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfirmModal({ isOpen: true, action: 'reset' });
  };

  const handleComplete = () => {
    setConfirmModal({ isOpen: true, action: 'complete' });
  };

  // Handle confirm action from modal
  const handleConfirmAction = async () => {
    setConfirmLoading(true);

    try {
      if (confirmModal.action === 'reset') {
        // Reset all players to default values
        setSaving(true);

        const resetPlayers = players.map(player => ({
          ...player,
          buyins: 1,
          leftoutChips: 0
        }));

        setPlayers(resetPlayers);

        if (game?.id) {
          await Promise.all(resetPlayers.map(player =>
            pokerGameAPI.upsertResult(game?.id, player.id, {
              initialBuyin: 1,              // Raw buyin count (not dollars!)
              finalChips: 0                  // Raw chip count (not dollars!)
            })
          ));
        }

        setGameFinished(false);
        toast.success('Scoring reset successfully');
      } else if (confirmModal.action === 'complete') {
        // Mark game as complete - call the existing handler
        await handleMarkComplete();
      }
    } catch (error) {
      console.error('Error in confirm action:', error);
      toast.error(confirmModal.action === 'reset' ? 'Error resetting' : 'Error deleting game');
    } finally {
      setSaving(false);
      setConfirmLoading(false);
      setConfirmModal({ isOpen: false, action: null });
    }
  };

  // Get confirm modal props
  const getConfirmModalProps = () => {
    if (confirmModal.action === 'reset') {
      return {
        title: 'Reset Scoring',
        message: 'Are you sure you want to reset the scoring? This will clear all player data and reset to defaults.',
        confirmText: 'Reset',
        confirmStyle: 'warning'
      };
    } else if (confirmModal.action === 'complete') {
      return {
        title: 'Complete Game',
        message: 'Are you sure you want to mark this game as complete? This will finalize the results and create a settlement in the Expenses tab.',
        confirmText: 'Complete',
        confirmStyle: 'primary'
      };
    }
    return {};
  };

  // Generate scorecard text for sharing


  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
        {/* Header */}
        <EventSummary
          title="Poker Scoring"
          eventId={event?.id}
          eventData={event}
          description="Track buy-ins and calculate settlements"
          status={game?.id ? { label: isViewMode ? 'COMPLETED' : game?.status === 'paused' ? 'PAUSED' : 'ACTIVE', variant: isViewMode ? 'completed' : game?.status === 'paused' ? 'warning' : 'active' } : null}
          onShareLink={game?.id ? onCopyLink : null}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className={theme.textMuted}>Loading game data...</p>
        </div>
      )}


      {/* Main content - only show when not loading */}
      {!loading && (
        <>
          {/* No players warning — show when no available members in attendance */}
          {players.length === 0 && getAvailableMembers(members || [], event).length === 0 && (
            <div className={`mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl`}>
              <p className={`text-sm text-amber-800 font-medium`}>
                ⚠️ No members are available for this event.
              </p>
              <p className={`text-sm text-amber-700 mt-2`}>
                Use the <strong>"Event Attendance"</strong> section above to mark members as "Available" or "Join Late", then return here to start scoring.
              </p>
            </div>
          )}

          {/* Game Settings */}
      <div className={`mb-6 p-4 ${theme.backgroundSecondary} rounded-xl border ${theme.border}`}>
        {/* Debug info - Table Name and Game ID */}
        <div className={`mb-3 pb-3 border-b ${theme.border} flex flex-wrap gap-4 text-xs`}>
          <span className={`${theme.textSecondary}`}>
            <strong>Table:</strong> {game?.tableName || event?.subject || 'N/A'}
          </span>
          <span className={`${theme.textSecondary}`}>
            <strong>Game ID:</strong> {game?.id || 'N/A'}
          </span>
          <span className={`${theme.textSecondary}`}>
            <strong>Event ID:</strong> {event?.id || 'N/A'}
          </span>
          <span className={`${theme.textSecondary}`}>
            <strong>Game Status:</strong> {game?.status || 'N/A'}
          </span>
          <span className={`${isViewMode ? 'text-red-500' : 'text-green-500'}`}>
            <strong>View Mode:</strong> {isViewMode ? 'YES (locked)' : 'NO (editable)'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <span className={`text-sm font-semibold ${theme.text}`}>Game Settings:</span>

          {/* Chip Value - inline */}
          <div className="flex items-center gap-2">
            <span className={`text-sm ${theme.textSecondary}`}>1 Chip =</span>
            <div className="flex items-center">
              <span className={`text-sm ${theme.textSecondary} mr-1`}>$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={gameSettings.chipValue === '' ? '' : gameSettings.chipValue}
                onChange={(e) => updateGameSettings('chipValue', e.target.value === '' ? '' : parseFloat(e.target.value))}
                onBlur={(e) => { if (e.target.value === '') updateGameSettings('chipValue', 0); }}
                disabled={isViewMode}
                className={`w-16 px-2 py-1 border ${theme.border} rounded ${theme.input} text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
              />
            </div>
          </div>

          {/* Buy-in Amount - inline */}
          <div className="flex items-center gap-2">
            <span className={`text-sm ${theme.textSecondary}`}>1 Buyin =</span>
            <input
              type="number"
              min="0"
              value={gameSettings.buyIn}
              onChange={(e) => updateGameSettings('buyIn', e.target.value === '' ? '' : parseInt(e.target.value))}
              onBlur={(e) => { if (e.target.value === '') updateGameSettings('buyIn', 0); }}
              disabled={readOnly}
              className={`w-20 px-2 py-1 border ${theme.border} rounded ${theme.input} text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
            />
            <span className={`text-sm ${theme.textSecondary}`}>chips</span>
          </div>
        </div>
      </div>

      {/* Score Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b-2 ${theme.border} ${theme.backgroundSecondary}`}>
              <th className={`text-left py-3 px-4 font-semibold ${theme.text}`}>Player</th>
              <th className={`text-center py-3 px-4 font-semibold ${theme.text}`}>Buyins</th>
              {gameFinished && (
                <>
                  <th className={`py-3 px-4 font-semibold ${theme.text}`}>
                    <div className="flex justify-center">Left</div>
                  </th>
                  <th className={`text-center py-3 px-4 font-semibold ${theme.text}`}>Net</th>
                  <th className={`text-center py-3 px-4 font-semibold ${theme.text}`}>Net $</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {players.map((player, index) => (
              <tr key={player.id} className={`border-b ${theme.border} ${index % 2 === 0 ? theme.backgroundSecondary : ''}`}>
                {/* Player Name */}
                <td className={`py-4 px-4 ${theme.text} font-medium`}>
                  {getDisplayName(player)}
                </td>

                {/* Buyins */}
                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-x-2">
                    <button
                      onClick={() => updatePlayer(player.id, 'buyins', player.buyins - 1)}
                      className={`p-1 rounded hover:bg-red-100 ${theme.text} disabled:opacity-50 disabled:cursor-not-allowed`}
                      disabled={player.buyins <= 0 || readOnly || !game?.id}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className={`w-12 text-center font-semibold ${theme.text}`}>
                      {player.buyins}
                    </span>
                    <button
                      onClick={() => updatePlayer(player.id, 'buyins', player.buyins + 1)}
                      className={`p-1 rounded hover:bg-green-100 ${theme.text} disabled:opacity-50 disabled:cursor-not-allowed`}
                      disabled={readOnly || !game?.id}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </td>

                {/* Leftout Chips (only after game finished) */}
                {gameFinished && (
                  <td className="py-4 px-4">
                    <div className="flex justify-center">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={player.leftoutChips}
                        onChange={(e) => {
                          let val = e.target.value;
                          // Allow numbers and one decimal point
                          val = val.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = val.split('.');
                          if (parts.length > 2) {
                            val = parts[0] + '.' + parts.slice(1).join('');
                          }
                          // skipSave=true: don't call the API on every keystroke to avoid
                          // racing writes. The onBlur handler below performs the actual save.
                          if (val === '' || val === '.') {
                            updatePlayer(player.id, 'leftoutChips', val, { skipSave: true });
                          } else {
                            // Parse as float to allow decimals
                            const numVal = parseFloat(val);
                            if (!isNaN(numVal)) {
                              updatePlayer(player.id, 'leftoutChips', val, { skipSave: true });
                            }
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); e.target.blur(); } }}
                        onFocus={(e) => e.target.select()}
                        onBlur={(e) => {
                          const val = e.target.value;
                          if (val === '' || val === '.') {
                            updatePlayer(player.id, 'leftoutChips', 0);
                          } else {
                            // Clean up trailing decimal point
                            const numVal = parseFloat(val);
                            if (!isNaN(numVal)) {
                              updatePlayer(player.id, 'leftoutChips', numVal);
                            } else {
                              updatePlayer(player.id, 'leftoutChips', 0);
                            }
                          }
                        }}
                        disabled={readOnly}
                        className={`w-20 px-2 py-1 text-center border ${theme.border} rounded ${theme.input} disabled:opacity-50 disabled:cursor-not-allowed`}
                      />
                    </div>
                  </td>
                )}

                {/* Net Chips and Net $ (only after game finished) */}
                {gameFinished && (
                  <>
                    <td className={`py-4 px-4 text-center font-bold ${
                      calculateResultInChips(player) > 0 ? 'text-green-600' :
                      calculateResultInChips(player) < 0 ? 'text-red-600' :
                      theme.text
                    }`}>
                      {calculateResultInChips(player) > 0 ? '+' : ''}
                      {calculateResultInChips(player)}
                    </td>
                    <td className={`py-4 px-4 text-center font-bold ${
                      calculateResult(player) > 0 ? 'text-green-600' :
                      calculateResult(player) < 0 ? 'text-red-600' :
                      theme.text
                    }`}>
                      {calculateResult(player) > 0 ? '+' : ''}
                      ${calculateResult(player).toFixed(2)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>

          {/* Summary Row */}
          <tfoot>
            <tr className={`border-t-2 ${theme.border} ${theme.backgroundSecondary}`}>
              <td className={`py-4 px-4 font-bold ${theme.text}`}>TOTAL</td>
              <td className={`py-4 px-4 text-center font-bold ${theme.text}`}>
                {summary.totalBuyIns}
              </td>
              {gameFinished && (
                <>
                  <td className={`py-4 px-4 text-center font-bold ${theme.text}`}>
                    {summary.totalLeftoutChips}
                  </td>
                  <td className={`py-4 px-4 text-center font-bold ${
                    Math.abs(summary.totalResultInChips) < 0.01 ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}>
                    {summary.totalResultInChips.toFixed(2)}
                  </td>
                  <td className={`py-4 px-4 text-center font-bold ${
                    Math.abs(summary.totalResult) < 0.01 ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}>
                    {Math.abs(summary.totalResult) < 0.01 ? '✓' : `$${summary.totalResult.toFixed(2)}`}
                  </td>
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-end items-center flex-wrap gap-2">
        <div className="flex flex-wrap gap-2">
          {isViewMode ? (
            /* Game completed — show Close for everyone */
            <button
              onClick={onClose}
              className={`px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium`}
            >
              Close
            </button>
          ) : !isScorer ? null : !gameFinished ? (
            /* Scorer — active game */
            <>
              <button
                onClick={handleReset}
                disabled={saving || paused}
                className={`px-3 py-1.5 text-sm border ${theme.border} rounded-lg hover:${theme.backgroundSecondary} transition-colors ${theme.text} disabled:opacity-50`}
              >
                Reset
              </button>
              {!paused && (
                <button
                  onClick={handleFinishGame}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center gap-x-1.5 disabled:opacity-50"
                >
                  <Calculator className="w-4 h-4" />
                  <span>Finish</span>
                </button>
              )}
            </>
          ) : (
            /* Scorer — finished but not yet completed */
            <>
              <button
                type="button"
                onClick={async () => {
                  setGameFinished(false);
                  if (game?.id) {
                    try { await pokerGameAPI.updateTable(game.id, { status: 'active' }); } catch (e) { /* non-critical */ }
                  }
                }}
                className={`px-3 py-1.5 text-sm border ${theme.border} rounded-lg hover:${theme.backgroundSecondary} transition-colors ${theme.text}`}
              >
                Back
              </button>
              {/* Mark Complete button - only show when chips are balanced */}
              <button
                type="button"
                onClick={handleComplete}
                disabled={Math.abs(summary.totalResultInChips) >= 0.01 || saving || paused}
                className="px-3 py-1.5 text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors font-medium disabled:opacity-50"
              >
                ✓ Complete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Warning if not balanced */}
      {gameFinished && isScorer && !isViewMode && Math.abs(summary.totalResultInChips) >= 0.01 && (
        <div className={`mt-4 p-4 ${theme.backgroundSecondary} border-2 ${theme.border} rounded-lg`}>
          <p className={`text-sm ${theme.text} font-medium`}>
            ⚠️ Total result is not balanced. Please verify leftout chips before saving.
          </p>
        </div>
      )}
        </>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null })}
        onConfirm={handleConfirmAction}
        loading={confirmLoading}
        {...getConfirmModalProps()}
      />

    </div>
  );
};

export default PokerScoring;
